import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@heroui/input";
import { Button } from "@heroui/button";
import { Link } from "react-router-dom";
import { Terminal } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { apiClient } from "@/api/api.ts";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";

export default function Component() {
  const [email, setEmail] = useState("");

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationKey: ["credentials"],
    mutationFn: async ({ email }: { email: string }) => {
      const response = await apiClient.post("/passwordForgot", { email });

      return response.data;
    },
    onSuccess: () => {
      toast.success("Password reset instructions sent! Please check your email.");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send reset email. Please try again.";
      toast.error(msg);
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    mutateAsync({ email });
  }

  return (
    <div className="flex min-h-[calc(100vh-80px)] w-full bg-cyan-50/50 flex-col items-center justify-center px-4 py-8 dark:bg-gray-950">
      <div className="mx-auto w-full max-w-md space-y-6">
        <div>
          {isSuccess && (
            <Alert className="mb-4 bg-emerald-50 border-emerald-300 text-emerald-800 animate-scale-up-center">
              <Terminal className="h-4 w-4" />
              <AlertTitle>Email Sent!</AlertTitle>
              <AlertDescription>
                Please check your email for the password reset link.
              </AlertDescription>
            </Alert>
          )}
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
            Forgot your password?
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Enter the email address associated with your account and we'll send
            you a link to reset your password.
          </p>
        </div>
        <form className="space-y-6" onSubmit={onSubmit}>
          <div>
            <Label htmlFor="email" className="sr-only">
              Email address
            </Label>
            <Input
              id="email"
              name="email"
              radius={"sm"}
              variant="flat"
              type="email"
              autoComplete="email"
              required
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            className="w-full rounded bg-[#008c8fff] text-white"
            isLoading={isPending}
          >
            Send Reset Link
          </Button>
        </form>
        <div className="flex justify-center">
          <Link
            to="/login"
            className="text-sm font-medium text-[#008c8fff] hover:underline"
          >
            &larr; Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
